import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {type CSSProperties, type MutableRefObject, useEffect, useRef} from 'react'
import {Transition, type TransitionStatus} from 'react-transition-group'

import {CommitState} from '../../hooks/use-autosave'
import {RequestStateIcon} from '../common/state-style-decorators'
import styles from './autosave-message.module.css'

type AutosaveMessageProps = {
  commitState: CommitState
  errorMessage: MutableRefObject<string>
}

const defaultStyle = {
  opacity: 0,
}

const duration = 300

const transitionStyles: Record<TransitionStatus, CSSProperties> = {
  entering: {opacity: 1},
  entered: {opacity: 1},
  exiting: {opacity: 0, transition: `opacity ${duration}ms ease-in-out`},
  exited: {opacity: 0},
  unmounted: {opacity: 0},
}

export const AutosaveMessage = ({commitState, errorMessage}: AutosaveMessageProps) => {
  const saveNodeRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (commitState === CommitState.Successful) {
      announce('Saved!')
    }
  }, [commitState])

  return (
    <div className={styles.AutosaveMessageContainer}>
      <>
        <Transition in={commitState === CommitState.Successful} nodeRef={saveNodeRef} timeout={500}>
          {state => (
            <span
              className={styles.MessageText}
              ref={saveNodeRef}
              style={{
                ...defaultStyle,
                ...transitionStyles[state],
              }}
            >
              <RequestStateIcon status={CommitState.Successful} />
              <span className={styles.SavedMessageText} {...testIdProps('column-settings-saved-message')}>
                Saved!
              </span>
            </span>
          )}
        </Transition>
        {commitState === CommitState.Failed && (
          <>
            <RequestStateIcon status={CommitState.Failed} />
            <span className={styles.ErrorMessageText} {...testIdProps('single-select-options-error-box')}>
              {errorMessage.current}
            </span>
          </>
        )}
      </>
    </div>
  )
}
